import { AdminRole } from './admin.model';

export type AdminSection =
  | 'categories'
  | 'questions'
  | 'golden_points'
  | 'favorite_lists'
  | 'users'
  | 'packages'
  | 'codes'
  | 'subscriptions'
  | 'notifications'
  | 'reports'
  | 'app_content';

export type AdminAction = 'read' | 'write' | 'delete';

export const ADMIN_SECTIONS: { key: AdminSection; label: string }[] = [
  { key: 'categories', label: 'Categories' },
  { key: 'questions', label: 'Questions & Answers' },
  { key: 'golden_points', label: 'Golden Points' },
  { key: 'favorite_lists', label: 'Favorite Lists' },
  { key: 'users', label: 'Users' },
  { key: 'packages', label: 'Packages' },
  { key: 'codes', label: 'Codes' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'reports', label: 'Reports' },
  { key: 'app_content', label: 'App Content & FAQ' },
];

export const ADMIN_ACTIONS: { key: AdminAction; label: string }[] = [
  { key: 'read', label: 'Read' },
  { key: 'write', label: 'Write' },
  { key: 'delete', label: 'Delete' },
];

/** sections whose content can be limited to specific categories */
export const CATEGORY_SCOPED_SECTIONS: AdminSection[] = [
  'categories',
  'questions',
  'golden_points',
];

export const ADMIN_ROLES: {
  key: AdminRole;
  label: string;
  description: string;
  actions: AdminAction[];
}[] = [
  {
    key: 'SUPER_ADMIN',
    label: 'Super Admin',
    description: 'Full access to everything, including managing admins.',
    actions: ['read', 'write', 'delete'],
  },
  {
    key: 'ADMIN',
    label: 'Admin',
    description: 'Can read, write and delete in the selected sections.',
    actions: ['read', 'write', 'delete'],
  },
  {
    key: 'EDITOR',
    label: 'Editor',
    description: 'Can read and write in the selected sections, no delete.',
    actions: ['read', 'write'],
  },
  {
    key: 'VIEWER',
    label: 'Viewer',
    description: 'Read only in the selected sections.',
    actions: ['read'],
  },
];

export function roleLabel(role?: AdminRole | null): string {
  return ADMIN_ROLES.find((r) => r.key === role)?.label ?? role ?? '';
}

export function roleActions(role: AdminRole): AdminAction[] {
  return ADMIN_ROLES.find((r) => r.key === role)?.actions ?? [];
}

export function hasPermission(
  user: { role?: AdminRole; permissions?: string[] } | null | undefined,
  section: AdminSection,
  action: AdminAction = 'read'
): boolean {
  if (!user) {
    return false;
  }
  if (user.role === 'SUPER_ADMIN') {
    return true;
  }
  return (
    roleActions(user.role!).includes(action) &&
    (user.permissions ?? []).includes(`${section}.${action}`)
  );
}
